import { SendOutlined, MessageFilled, CloseOutlined } from '@ant-design/icons';
import { OrderStateType } from '../../../types/OrderStateType';
import './ChatContainer.scss';
import { useState, useMemo, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTimeAgo from 'react-time-ago';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../../../assets/api/endpoints';
import { getUserInfo } from '../../../../../helpers/localStorageHandler';
import { MessageType } from '../../../types/MessageType';
import MessageItem from './MessageItem/MessageItem';
import Stomp from 'stompjs';
import TextArea from 'antd/es/input/TextArea';
import { Card, Form, Grid, Skeleton } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { OrderDetailsType } from '../../../types/OrderDetailsType';
import { useDirectionContext } from '../../../../../store/DirectionContext';
import { getLanguage } from '../../../../../helpers/getLanguage';
import { getUserLanguageInCookie } from '../../../../../helpers/cookiesHandler';
import EmojiPicker from 'emoji-picker-react';
import { SmileOutlined } from '@ant-design/icons';
import { INPUT_CHAT_VALIDATION } from '../../../../../assets/config/validation-regex';
import { useConfigurationContext } from '../../../../../store/configurationContext';
import FileUploader from './FileUploader';
type ChatContainerProps = {
	isMerchant: boolean;
	orderNumber: string;
	orderState: OrderStateType;
	userName: string;
	orders: number;
	reviews: number;
	rating: number;
	stompClient: Stomp.Client | null;
	getOrderDetails: () => void;
	setIsActionsDisabled: Dispatch<SetStateAction<boolean>>;
	clientOrder: number;
	clientName: string;
	appealTask: OrderDetailsType['appealTask'];
	clientId: string;
	merchantId: string;
};

const iconStyle = {
	borderRadius: '50%',
	width: '26px',
	height: '26px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	padding: '5px',
};
const UNREAD_MESSAGES = 99;
function ChatContainer({
	isMerchant,
	orderNumber,
	orderState,
	userName,
	orders,
	reviews,
	rating,
	stompClient,
	getOrderDetails,
	setIsActionsDisabled,
	clientOrder,
	clientName,
	appealTask,
	clientId,
	merchantId,
}: ChatContainerProps) {
	const location = useLocation();
	const textField = useRef<null | HTMLElement>(null);
	const { t } = useTranslation();
	const { xs, lg } = Grid.useBreakpoint();
	const { configurationState } = useConfigurationContext();
	const [chatForm] = Form.useForm();
	const [showChat, setShowChat] = useState({ value: true });
	const bottomRef = useRef<null | HTMLElement>(null);
	const myUserName = useMemo(() => getUserInfo()?.fullName, []);
	const userInfo = useMemo(() => getUserInfo(), []);
	const [openEmojiPicker, setOpenEmojiPicker] = useState(false);
	const [disabledSend, setDisabledSend] = useState(false);

	const [clientLastSeen, setClientLastSeen] = useState<Date | null>(null);
	const [merchantLastSeen, setMerchantLastSeen] = useState<Date | null>(null);
	const isChatDisabled = useMemo(
		() => orderState === 'CANCELLED' || orderState === 'REJECTED',
		[orderState, location.search],
	);
	const [isChatTerminated, setIsChatTernimated] = useState(false);
	const isIncomingMessge = (msg: any) => msg.role !== loggedInUserRole;
	const loggedInUserRole = useMemo(() => getUserInfo()?.role || 'ADMIN', []);
	const [isUserMerchantOnline, setIsUserMerchantOnline] = useState(false);
	const [isUserClientOnline, setIsUserClientOnline] = useState(false);
	const [historicalMessages, setHistoicalMessages] = useState<MessageType[]>([]);
	const [liveMessages, setLiveMessages] = useState<MessageType[]>([]);
	const [isChatConnected, setIsChatConnected] = useState(false);
	const [text, setText] = useState('');
	const { directionState } = useDirectionContext();
	const [unreadMessages, setUnreadMessages] = useState(0);
	const chatTopic = useMemo(
		() => API_ENDPOINTS.getOrderChatMsgTopic.replace('%orderNumber%', orderNumber),
		[orderNumber, location.search],
	);
	const sendTopic = useMemo(
		() => API_ENDPOINTS.sendOrderChatMsgTopic.replace('%orderNumber%', orderNumber),
		[orderNumber, location.search],
	);

	const checkAppealEnable = useMemo(() => {
		if (orderState === 'APPEAL' && userInfo?.role !== 'SUPER_USER') {
			return appealTask?.assigneeId !== userInfo?.id;
		}
		return false;
	}, [orderState, location.search, appealTask]);

	useEffect(() => {
		// Start the WS connect in case it's allowed
		if (!isChatDisabled && !isChatTerminated && stompClient?.connected) {
			setIsChatConnected(true);
			setIsActionsDisabled(false);
			stompClient?.subscribe(chatTopic, onMessageReceived);
			stompClient?.send(sendTopic, {}, JSON.stringify({ sender: userName, type: 'JOIN' }));
		}
	}, [stompClient, location.search]);
	const getChatHistory = () => {
		axios.get(API_ENDPOINTS.getChats.replace('%orderNumber%', orderNumber)).then(async (response) => {
			const messages: MessageType[] = response.data.chatMessages;
			const doesContainAction = messages?.some((message) => message.type === 'ACTION');
			setIsChatTernimated(!doesContainAction && orderState === 'EXPIRED');
			setClientLastSeen(response.data.clientLastActive);
			setMerchantLastSeen(response.data.merchantLastActive);
			setHistoicalMessages(messages);
		});
	};

	useEffect(() => {
		getChatHistory();
	}, []);

	useEffect(() => {
		if (isChatConnected && stompClient?.connected && (isChatDisabled || isChatTerminated)) {
			stompClient?.disconnect(
				() => {
					setIsChatConnected(false);
					setIsActionsDisabled(true);
				},
				{
					orderNumber,
				},
			);
		}
	}, [isChatDisabled, isChatTerminated, location.search]);

	const onMessageReceived = (data: any) => {
		const newMessage: MessageType = JSON.parse(data.body);
		setLiveMessages((oldMessages) => [...oldMessages, newMessage]);

		if (isIncomingMessge(newMessage)) {
			if (newMessage.messageId && newMessage.role == loggedInUserRole) {
				setHistoicalMessages((messages) =>
					messages.map((message) => ({ ...message, delivered: newMessage.delivered })),
				);
				setLiveMessages((messages) => messages.map((message) => ({ ...message, delivered: newMessage.delivered })));
			}
			if (newMessage.type === 'DELIVERED') {
				setHistoicalMessages((messages) => messages.map((message) => ({ ...message, delivered: true })));
				setLiveMessages((messages) => messages.map((message) => ({ ...message, delivered: true })));
			}
			if (
				newMessage.type === 'TEXT' ||
				newMessage.type === 'FILE' ||
				newMessage.type === 'ACTION' ||
				newMessage.type === 'JOIN'
			) {
				setShowChat((prevState) => {
					if (prevState.value) {
						sendReceivedNotification(newMessage);
					} else {
						setUnreadMessages((count) => count + 1);
					}
					return prevState;
				});
			}
			if (newMessage.type === 'RECEIVED' || newMessage.type === 'JOIN') {
				if (newMessage.role === 'MERCHANT') {
					setIsUserMerchantOnline(true);
				}
				if (newMessage.role === 'CLIENT') {
					setIsUserClientOnline(true);
				}
			} else if (newMessage.type === 'LEAVE') {
				if (newMessage.role === 'MERCHANT') {
					setIsUserMerchantOnline(false);
					setMerchantLastSeen(new Date());
				}
				if (newMessage.role === 'CLIENT') {
					setIsUserClientOnline(false);
					setClientLastSeen(new Date());
				}
			}
		}

		if (newMessage.type === 'TERMINATED') {
			setIsChatTernimated(true);
		} else if (newMessage.type === 'ACTION') {
			getOrderDetails();
		}

		scrollToChatBottom();
	};

	useEffect(() => {
		if (isUserClientOnline && isUserMerchantOnline) {
			setHistoicalMessages((messages) => messages.map((message) => ({ ...message, read: true })));
			setLiveMessages((messages) => messages.map((message) => ({ ...message, read: true })));
		}
	}, [isUserClientOnline, isUserMerchantOnline, liveMessages]);
	const onChatKeyUp = (event: any) => {
		event.preventDefault();

		if (event.keyCode === 13 && !disabledSend) {
			onMessageSend();
		}
	};

	const onMessageSend = () => {
		const content = chatForm.getFieldValue('currentMessage');

		if (stompClient && content.trim()) {
			stompClient?.send(sendTopic, {}, JSON.stringify({ sender: myUserName, content, type: 'TEXT' }));
			chatForm.setFieldValue('currentMessage', '');
		}
	};

	const sendReceivedNotification = (message: any) => {
		stompClient?.send(
			sendTopic,
			{},
			JSON.stringify({ messageId: message.messageId, role: loggedInUserRole, type: 'RECEIVED' }),
		);
	};

	const scrollToChatBottom = () => {
		setTimeout(() => {
			bottomRef?.current?.scroll({
				top: 100000,
				left: 0,
				behavior: 'smooth',
			});
		}, 500);
	};

	const handleUploadFileClick = (event: any, type: 'IMAGE' | 'FILE') => {
		const files = event.target?.files;
		const uploadedFile = files[0];

		if (!uploadedFile) {
			return;
		}

		const url = type === 'IMAGE' ? API_ENDPOINTS.uploadImage + orderNumber : API_ENDPOINTS.uploadFile + orderNumber;
		const formData = new FormData();

		formData.append('file', uploadedFile);

		axios
			.post(url, formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			})
			.then((result) => {
				if (result && stompClient) {
					const chatMessage = {
						sender: userName,
						content: '',
						type: type,
						fileId: result.data.fileId,
						fileLabel: result.data.fileLabel,
						fileUrl: result.data.fileUrl ?? undefined,
					};
					stompClient.send(sendTopic, {}, JSON.stringify(chatMessage));
				}
			})
			.catch((error) => console.error(error));
	};
	useEffect(() => {
		if (showChat.value) {
			const historicalMessagesReversed = [...historicalMessages].reverse();
			if (historicalMessagesReversed[0] && stompClient?.connected) {
				sendReceivedNotification(historicalMessagesReversed[0]);
			}
		} else {
			setUnreadMessages(0);
		}
	}, [showChat]);

	const handleEmojiClick = (emojiData: any) => {
		setOpenEmojiPicker(false);
		const content = chatForm.getFieldValue('currentMessage') ?? '';
		setText((prevMessage) => prevMessage + emojiData.emoji);
		chatForm.setFieldValue('currentMessage', content + emojiData.emoji);
		textField.current?.focus();
	};
	useEffect(() => setShowChat({ value: !Boolean(xs) }), [xs]);
	const themeColor = configurationState.find((item) => item.configKey === 'theme_color')?.value || '';
	const secondary_color = configurationState.find((item) => item.configKey === 'secondary_color')?.value || '';
	return (
		<div className='chatting-with-other-party-container '>
			{orderState ? (
				<>
					{showChat.value ? null : (
						<button
							style={{
								left: directionState === 'rtl' ? '20px' : 'unset',
								right: directionState === 'ltr' ? '20px' : 'unset',
								backgroundColor: themeColor,
							}}
							className='open-chat-button rounded-xl'
							onClick={() => setShowChat({ value: true })}
						>
							{unreadMessages ? (
								<span
									className='bill-icon'
									style={{
										border: `1px solid ${secondary_color}`,
										backgroundColor: themeColor,
									}}
								>
									{unreadMessages > UNREAD_MESSAGES ? `+${UNREAD_MESSAGES}` : unreadMessages}
								</span>
							) : (
								<span className='bill-icon-empty'></span>
							)}
							<MessageFilled style={{ color: secondary_color }} />
						</button>
					)}
					<div className={`order-life-cycle-chat-container ${showChat.value ? '' : 'hidden'}`}>
						<div className='order-life-cycle-chat-box-header' style={{ backgroundColor: themeColor }}>
							<div className='header-container'>
								<div className='user-details' style={{ color: secondary_color }}>
									<div className='use-name-live-state'>
										<div>
											<p className='flex'>
												<Link
													to={`/merchant/?id=${merchantId}`}
													className='flex items-center'
													style={{ color: secondary_color }}
													id='olc_merchantName'
												>
													{userName}
												</Link>
												{isUserMerchantOnline ? (
													<span className='live text-[10px]'>{t<string>('live')}</span>
												) : merchantLastSeen ? (
													<span className='last-seen text-[10px]'>
														<ReactTimeAgo
															date={new Date(merchantLastSeen).getTime()}
															locale={getLanguage(getUserLanguageInCookie())}
														/>
													</span>
												) : null}
											</p>
											<p className='flex'>
												<Link
													to={`/client/?id=${clientId}`}
													className='flex items-center '
													style={{ color: secondary_color }}
													id='olc_clientName'
												>
													{clientName}
												</Link>
												{isUserClientOnline ? (
													<span className='live text-[10px] text-[#acb8b1]' id='chat_status'>
														{t<string>('live')}
													</span>
												) : clientLastSeen ? (
													<span className='last-seen text-[10px] text-[#acb8b1]' id='chat_status'>
														<ReactTimeAgo
															date={new Date(clientLastSeen).getTime()}
															locale={getLanguage(getUserLanguageInCookie())}
														/>
													</span>
												) : null}
											</p>
										</div>
										<div className='flex justify-between gap-1'>
											<p className='user-name'>{t<string>('supportRoom')}</p>
											<CloseOutlined
												className='close w-auto pt-1 pl-1 justify-end text-[12px]'
												alt='close'
												onClick={() => setShowChat({ value: false })}
											/>
										</div>
									</div>
								</div>
							</div>
							<div></div>
						</div>
						<div className='order-life-cycle-chat-box-body' ref={bottomRef as any}>
							<div className='chat-message-container'>
								<Card
									className='chat-card'
									style={{
										width: 300,
										backgroundColor: themeColor,
										color: secondary_color,
										padding: '5px !important',
										opacity: '.8',
									}}
								>
									<p className='text-center text-[10px]'>
										<span className='text-[16px]'>👋</span>
										{t<string>('welcomeChatMessage')}
									</p>
								</Card>
							</div>
							{[...historicalMessages, ...liveMessages].map((message) => (
								<MessageItem
									isMerchant={isMerchant}
									message={message}
									merchantName={userName}
									clientName={clientName}
									orderNumber={orderNumber}
								/>
							))}
						</div>
						<div className='order-life-cycle-chat-box-footer'>
							{isChatDisabled ? (
								<span style={{ width: '100%', textAlign: 'center', fontSize: '14px', color: '#bdbdbd' }}>
									{t<string>('chatIsDisabled')}
								</span>
							) : (
								<Form
									name='chatForm'
									form={chatForm}
									className='chat-form'
									onValuesChange={() => setText(chatForm.getFieldValue('currentMessage')?.trim())}
								>
									<Form.Item style={{ width: '26px', display: 'flex', alignItems: 'center' }}>
										<FileUploader
											disabled={
												isChatDisabled ||
												isChatTerminated ||
												!isChatConnected ||
												checkAppealEnable ||
												!stompClient?.connected
											}
											style={{ ...iconStyle, color: secondary_color, backgroundColor: themeColor }}
											handleUploadFileClick={handleUploadFileClick}
										/>
									</Form.Item>
									<Form.Item
										name='currentMessage'
										rules={[
											{
												validator: (_, value) => {
													if (INPUT_CHAT_VALIDATION.test(value)) {
														setDisabledSend(false);
														return Promise.resolve();
													}
													setDisabledSend(true);
													return Promise.reject(new Error(t('validationChatMessage') as string));
												},
											},
										]}
									>
										<TextArea
											disabled={
												isChatDisabled ||
												isChatTerminated ||
												!isChatConnected ||
												checkAppealEnable ||
												!stompClient?.connected
											}
											ref={textField}
											onPressEnter={onChatKeyUp}
											placeholder={t<string>('typeMessageHere')}
											autoSize={{ minRows: 1, maxRows: 3 }}
											style={{ paddingInlineEnd: '30px' }}
										/>
									</Form.Item>
									{lg && (
										<SmileOutlined
											className='custom-icon'
											onClick={() => setOpenEmojiPicker((prvState) => !prvState)}
											style={{
												left: directionState === 'rtl' ? '60px' : 'unset',
												right: directionState === 'ltr' ? '60px' : 'unset',
											}}
										/>
									)}
									<EmojiPicker open={openEmojiPicker} onEmojiClick={handleEmojiClick} className='emoji-picker' />
									<button
										disabled={
											isChatDisabled ||
											isChatTerminated ||
											!isChatConnected ||
											!text ||
											checkAppealEnable ||
											disabledSend ||
											!stompClient?.connected
										}
										onClick={onMessageSend}
									>
										<SendOutlined
											style={{
												...iconStyle,
												backgroundColor: themeColor,
												color: secondary_color,
												transform: 'rotate(-30deg)',
												padding: '5px 5px 5px 7px',
											}}
										/>
									</button>
								</Form>
							)}
						</div>
					</div>
				</>
			) : (
				<Skeleton active paragraph={{ rows: 7 }} />
			)}
		</div>
	);
}

export default ChatContainer;
